import React, { useState } from "react";
import { ChevronDown, ChevronUp } from "lucide-react";
import { BaseMessage } from "../../types";
import { SearchCandidatsResult } from "./search_candidats";
import { SearchVillesResult } from "./search_villes";
import { SearchEnseignesResult } from "./search_enseignes";
import { SearchLocationsResult } from "./search_locations";
import { GeographicExpanderResult } from "./geographic_expander";

interface ToolMessageProps {
  message: BaseMessage;
}

export const ToolMessage: React.FC<ToolMessageProps> = ({
  message,
}) => {
  const { content, status, tool_call_result } = message;
  const [isExpanded, setIsExpanded] = useState(false);

  const isProgress = status === "progress";
  const isError = status === "error";
  const hasResults =
    tool_call_result && Object.keys(tool_call_result).length > 0;

  // Get component_key from tool_call_result or fallback to null
  const componentKey = tool_call_result?.component_key || null;

  // Render specific component based on component_key
  const renderToolResult = () => {
    if (!hasResults || !componentKey) {
      return null;
    }

    switch (componentKey) {
      case "search_candidats":
        return <SearchCandidatsResult {...tool_call_result} />;
      case "search_villes":
        return <SearchVillesResult {...tool_call_result} />;
      case "search_enseignes":
        return <SearchEnseignesResult {...tool_call_result} />;
      case "search_locations":
        return <SearchLocationsResult {...tool_call_result} />;
      case "geographic_expander":
        return (
          <GeographicExpanderResult
            {...tool_call_result}
          />
        );
      default:
        // Fallback for unknown component_key or missing component_key
        return (
          <div className="mt-2 rounded border border-gray-200 bg-white p-2">
            <pre className="overflow-auto text-xs">
              {JSON.stringify(tool_call_result, null, 2)}
            </pre>
          </div>
        );
    }
  };

  return (
    <div className="border-t border-slate-300 bg-slate-50">
      <div
        className={`flex items-center justify-between py-3 px-6 ${isExpanded ? "border-b border-slate-300" : ""}`}
      >
        <div className="flex flex-col">
          <div className="flex items-center">
            <span className="font-medium text-slate-600">Outil</span>
            {isProgress && (
              <span className="text-xs text-default-600">Recherche...</span>
            )}
            {isError && <span className="text-xs text-red-600">Erreur</span>}
          </div>
          {content && <div className="text-xs text-slate-500">{content}</div>}
        </div>

        {hasResults && (
          <div>
            <button
              onClick={() => setIsExpanded(!isExpanded)}
              className="text-slate-500 hover:text-slate-700 flex w-8 h-8 rounded-full bg-slate-100 ring-1 ring-slate-200 items-center justify-center"
            >
              {isExpanded ? (
                <ChevronDown className="h-6 w-6" />
              ) : (
                <ChevronUp className="h-6 w-6" />
              )}
            </button>
          </div>
        )}
      </div>
      {isExpanded && hasResults && (
        <div className="p-6">{renderToolResult()}</div>
      )}
    </div>
  );
};
